import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useIncrementProductStockBySku } from '../hooks/backend/inventoryPage'
import { useGetProductsCached } from '../hooks/cachedQueries'
import { playSound } from './ui/scanSounds'
import { toast } from '../lib/shadcn/sonner'
import { RadioGroup, RadioGroupItem } from '../lib/shadcn/radio-group'
import { Label } from '../lib/shadcn/label'
import { Input } from '../lib/shadcn/input'
import { Button } from '../lib/shadcn/button'
import { X, ChevronDown } from 'lucide-react'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '../lib/shadcn/collapsible'
import ProductTable, { type Product } from './ui/ProductTable'
import ProductFormDialog from './ui/ProductFormDialog'
import IngredientsPanel from './ui/IngredientsPanel'
import RecipeOnlyFlavorsPanel from './ui/RecipeOnlyFlavorsPanel'
import ProductionPanel from './ui/ProductionPanel'
import TierReviewPanel from './ui/TierReviewPanel'
import { PRODUCTS_CHANGED } from '../utils/hubSearchEvents'

export default function InventoryPage() {
  const location = useLocation()
  const tab =
    location.pathname === '/ingredients'
      ? 'ingredients'
      : location.pathname === '/production'
        ? 'production'
        : location.pathname === '/tier-review'
          ? 'tier-review'
          : 'products'

  const getProducts = useGetProductsCached()
  const incrementProductStockBySku = useIncrementProductStockBySku()

  // ─── Scan state ────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<'add' | 'remove'>('add')
  const [scanSku, setScanSku] = useState('')
  const [scanStatus, setScanStatus] = useState('Add mode: scan SKU to increment stock')
  const [scanOpen, setScanOpen] = useState(false)
  const scanInputRef = useRef<HTMLInputElement>(null)

  // ─── Product form dialog state ───────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [editingTab, setEditingTab] = useState<'details' | 'recipe'>('details')

  const products = (getProducts.data as Product[] | undefined) ?? []

  // On mount: load products and focus scan input
  useEffect(() => {
    getProducts.trigger()
    scanInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A product or recipe saved from the header search: refresh the table.
  useEffect(() => {
    const refresh = () => getProducts.trigger(undefined, { skipCache: true })
    window.addEventListener(PRODUCTS_CHANGED, refresh)
    return () => window.removeEventListener(PRODUCTS_CHANGED, refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Mode change ───────────────────────────────────────────────────────────
  const handleModeChange = (value: string) => {
    const newMode = value as 'add' | 'remove'
    setMode(newMode)
    setScanStatus(
      newMode === 'add'
        ? 'Add mode: scan SKU to increment stock'
        : 'Remove mode: scan SKU to decrement stock',
    )
    scanInputRef.current?.focus()
  }

  // ─── Scan / submit ─────────────────────────────────────────────────────────
  const handleScan = async () => {
    const sku = scanSku.trim()
    if (!sku) {
      playSound('error')
      setScanStatus('Please scan a SKU.')
      return
    }
    setScanStatus('Updating…')
    try {
      const result = await incrementProductStockBySku.trigger({ mode, sku })
      const rows = result as Array<{ sku: string; qty_in_stock: number }> | undefined
      const updated = rows?.[0]
      setScanStatus(`Updated SKU ${sku}: In stock = ${updated?.qty_in_stock ?? '?'}`)
      playSound('success')
      getProducts.trigger(undefined, { skipCache: true })
      setScanSku('')
      scanInputRef.current?.focus()
    } catch {
      setScanStatus('Update failed. Please try again.')
      playSound('error')
      toast.error('Failed to update stock. Please try again.')
      scanInputRef.current?.focus()
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6 space-y-6 min-h-screen bg-background">
      <h1 className="text-2xl font-bold tracking-tight">{tab === 'ingredients' ? 'Ingredients' : tab === 'production' ? 'Production' : tab === 'tier-review' ? 'Tier review' : 'Products'}</h1>
      <div className="space-y-6">
        {tab === 'products' && <div className="space-y-6">
          {/* ── Scan to Adjust Stock ─────────────────────────────────────────── */}
          <Collapsible
        open={scanOpen}
        onOpenChange={open => {
          setScanOpen(open)
          if (open) setTimeout(() => scanInputRef.current?.focus(), 0)
        }}
        className="rounded-lg border bg-card text-card-foreground shadow-sm max-w-xl"
      >
        <CollapsibleTrigger className="flex w-full items-center justify-between p-5 text-base font-semibold">
          Scan to Adjust Stock
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${scanOpen ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-5 pb-5 space-y-4">
        {/* Adjustment mode */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-muted-foreground">
            Adjustment mode
          </Label>
          <RadioGroup
            value={mode}
            onValueChange={handleModeChange}
            className="flex gap-6"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="add" id="mode-add" />
              <Label htmlFor="mode-add" className="cursor-pointer font-normal">
                Add to inventory
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="remove" id="mode-remove" />
              <Label htmlFor="mode-remove" className="cursor-pointer font-normal">
                Remove from inventory
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* SKU scan input */}
        <div className="space-y-1.5">
          <Label htmlFor="scan-sku" className="text-sm font-medium text-muted-foreground">
            Scan SKU
          </Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                id="scan-sku"
                ref={scanInputRef}
                value={scanSku}
                onChange={e => setScanSku(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void handleScan() }}
                placeholder="Scan or type a SKU…"
                className="pr-8"
                autoComplete="off"
              />
              {scanSku && (
                <button
                  onClick={() => { setScanSku(''); scanInputRef.current?.focus() }}
                  aria-label="Clear SKU"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              onClick={() => void handleScan()}
              disabled={incrementProductStockBySku.loading}
            >
              {incrementProductStockBySku.loading ? 'Updating…' : 'Submit'}
            </Button>
          </div>
        </div>

        {/* Status line */}
        <p className="text-sm text-muted-foreground">{scanStatus}</p>
        </CollapsibleContent>
      </Collapsible>

      {/* ── Product Inventory Table ──────────────────────────────────────── */}
      <ProductTable
        products={products}
        loading={getProducts.loading}
        onRefresh={() => getProducts.trigger(undefined, { skipCache: true })}
        onAddProduct={() => { setEditingProduct(null); setEditingTab('details'); setFormOpen(true) }}
        onEditProduct={(product, tab) => { setEditingProduct(product); setEditingTab(tab ?? 'details'); setFormOpen(true) }}
      />

      <ProductFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        product={editingProduct}
        initialTab={editingTab}
        onSaved={() => getProducts.trigger(undefined, { skipCache: true })}
      />
        </div>}

        {tab === 'ingredients' && <div className="space-y-6">
          <RecipeOnlyFlavorsPanel />
          <IngredientsPanel />
        </div>}

        {tab === 'production' && <div>
          <ProductionPanel />
        </div>}

        {tab === 'tier-review' && <div>
          <TierReviewPanel />
        </div>}
      </div>
    </div>
  )
}
